import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ArrowLeft, ArrowUpRight, Check } from 'lucide-react';
import { useRouter } from '../RouterContext';

const features = [
  {
    title: 'Agentes autónomos de IA',
    desc: 'Agentes inteligentes que executam tarefas operacionais de forma autónoma, sem intervenção manual constante.',
  },
  {
    title: 'Previsão de receitas',
    desc: 'Modelos preditivos que analisam histórico e padrões de mercado para gerar previsões de faturação fiáveis.',
  },
  {
    title: 'Previsão de produção',
    desc: 'Antecipação de necessidades de produção com base em dados reais — evita ruturas e excesso de stock.',
  },
  {
    title: 'Análise de dados em escala',
    desc: 'Processamento de grandes volumes de dados operacionais para identificar padrões e oportunidades de melhoria.',
  },
  {
    title: 'Integração com sistemas existentes',
    desc: 'Conecta-se com ERP, CRM e outras plataformas já em uso — sem substituir o que já funciona.',
  },
  {
    title: 'Dashboards de decisão',
    desc: 'Visualizações claras que transformam outputs dos agentes em recomendações accionáveis para a gestão.',
  },
];

const pillars = [
  'Automação de processos críticos',
  'Previsões com maior precisão',
  'Redução de decisões reativas',
  'Visibilidade sobre o futuro do negócio',
  'Crescimento sustentado e planeado',
  'Implementação acompanhada pela 2GF',
];

const faq = [
  {
    q: 'Para que tipo de empresa é a Axolote?',
    a: 'Para empresas que querem automatizar processos repetitivos e ganhar capacidade preditiva — desde PMEs industriais a empresas de serviços com operações complexas.',
  },
  {
    q: 'O que são agentes de IA?',
    a: 'São módulos de inteligência artificial que actuam de forma autónoma sobre dados e sistemas — executam tarefas, tomam micro-decisões e geram previsões sem precisar de intervenção constante.',
  },
  {
    q: 'A Axolote substitui a nossa equipa?',
    a: 'Não. A Axolote augmenta a capacidade da equipa — liberta as pessoas de tarefas repetitivas e fornece informação melhor para as decisões que realmente importam.',
  },
  {
    q: 'Quanto tempo demora a implementação?',
    a: 'Depende da complexidade dos processos a automatizar. Trabalhamos em fases incrementais para garantir valor desde cedo e ajustar com base na realidade da operação.',
  },
];

const AxolotePage: React.FC = () => {
  const { navigate } = useRouter();
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const [heroRef, heroInView] = useInView({ triggerOnce: true, threshold: 0.05 });
  const [featRef, featInView] = useInView({ triggerOnce: true, threshold: 0.05 });
  const [whyRef, whyInView] = useInView({ triggerOnce: true, threshold: 0.05 });
  const [faqRef, faqInView] = useInView({ triggerOnce: true, threshold: 0.05 });
  const [ctaRef, ctaInView] = useInView({ triggerOnce: true, threshold: 0.05 });

  const goContact = () => {
    navigate('home');
    setTimeout(() => document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' }), 80);
  };

  return (
    <div style={s.page}>

      {/* ── Navbar ── */}
      <nav style={s.nav}>
        <div style={s.navInner}>
          <button style={s.navLogo} onClick={() => navigate('home')}>
            <span style={s.navLogoMark}>2GF</span>
            <span style={s.navLogoText}>Innovation Systems</span>
          </button>
          <button style={s.backBtn} onClick={() => navigate('home')}>
            <ArrowLeft size={15} />
            Voltar ao site
          </button>
        </div>
      </nav>

      {/* ── Hero ── */}
      <section style={s.hero} ref={heroRef}>
        <div style={s.heroInner}>

          <motion.div
            style={s.heroText}
            initial={{ opacity: 0, y: 24 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.65 }}
          >
            <span style={s.badge}>Inteligência Artificial · Agentes · Previsão</span>
            <h1 style={s.heroTitle}>Axolote</h1>
            <p style={s.heroTagline}>
              A plataforma de agentes de IA para automatizar e antecipar o futuro do seu negócio.
            </p>
            <p style={s.heroBody}>
              A Axolote é uma plataforma de inteligência artificial baseada em agentes, desenvolvida
              para ajudar empresas a automatizar processos críticos e a prever receitas e produção
              com maior precisão.
            </p>
            <p style={s.heroBody}>
              Através de agentes inteligentes e autónomos, a plataforma executa tarefas operacionais,
              analisa dados em escala e gera previsões fiáveis para suportar a tomada de decisão.
            </p>
            <div style={s.heroActions}>
              <button style={s.btnPrimary} onClick={goContact}>
                Falar com a equipa
              </button>
              <button
                style={s.btnText}
                onClick={() => document.querySelector('#axolote-features')?.scrollIntoView({ behavior: 'smooth' })}
              >
                Ver funcionalidades <ArrowUpRight size={14} />
              </button>
            </div>
          </motion.div>

          <motion.div
            style={s.heroImgWrap}
            initial={{ opacity: 0, y: 24 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.65, delay: 0.15 }}
          >
            <img
              src="https://images.unsplash.com/photo-1677442135703-1787eea5ce01?w=900&q=85&auto=format&fit=crop&crop=center"
              alt="Inteligência Artificial"
              style={s.heroImg}
            />
          </motion.div>

        </div>
      </section>

      {/* ── Stats ── */}
      <div style={s.statsBar}>
        <div style={s.statsInner}>
          {[
            { val: 'Agentes', label: 'IA autónoma' },
            { val: 'Tempo real', label: 'Análise de dados' },
            { val: 'Previsão', label: 'Receitas e produção' },
          ].map((st, i) => (
            <div key={i} style={s.statItem}>
              <span style={s.statVal}>{st.val}</span>
              <span style={s.statLabel}>{st.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Features ── */}
      <section id="axolote-features" style={s.featuresSection} ref={featRef}>
        <div style={s.container}>
          <motion.div
            style={s.sectionHeader}
            initial={{ opacity: 0, y: 20 }}
            animate={featInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <span style={s.label}>Funcionalidades</span>
            <h2 style={s.sectionTitle}>Dados que se transformam em decisões estratégicas.</h2>
          </motion.div>

          <div style={s.featuresGrid}>
            {features.map((f, i) => (
              <motion.div
                key={i}
                style={s.featureItem}
                initial={{ opacity: 0, y: 20 }}
                animate={featInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.55, delay: 0.1 + i * 0.07 }}
              >
                <h3 style={s.featureTitle}>{f.title}</h3>
                <p style={s.featureDesc}>{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Porquê a Axolote ── */}
      <section style={s.whySection} ref={whyRef}>
        <div style={s.whyGrid}>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={whyInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <span style={s.label}>Porquê a Axolote</span>
            <h2 style={s.sectionTitle}>Planear com confiança e crescer de forma sustentada</h2>
            <p style={s.whyBody}>
              A maioria das empresas toma decisões com base em dados incompletos e análises retrospectivas.
              A Axolote muda isso — traz inteligência preditiva e automação para o centro da operação,
              sem exigir equipas de data science ou substituir os sistemas que já existem.
            </p>
          </motion.div>

          <div style={s.pillarsList}>
            {pillars.map((p, i) => (
              <motion.div
                key={i}
                style={s.pillarItem}
                initial={{ opacity: 0, x: 16 }}
                animate={whyInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.1 + i * 0.07 }}
              >
                <Check size={15} color="#7C3AED" strokeWidth={2.5} style={{ flexShrink: 0 }} />
                <span style={s.pillarText}>{p}</span>
              </motion.div>
            ))}
          </div>

        </div>
      </section>

      {/* ── FAQ ── */}
      <section style={s.faqSection} ref={faqRef}>
        <div style={s.faqContainer}>
          <motion.div
            style={s.sectionHeader}
            initial={{ opacity: 0, y: 20 }}
            animate={faqInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <span style={s.label}>Dúvidas</span>
            <h2 style={s.sectionTitle}>Perguntas frequentes</h2>
          </motion.div>

          <div style={s.faqList}>
            {faq.map((item, i) => (
              <motion.div
                key={i}
                style={s.faqItem}
                initial={{ opacity: 0, y: 12 }}
                animate={faqInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.1 + i * 0.07 }}
              >
                <button
                  style={s.faqQ}
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                >
                  <span>{item.q}</span>
                  <span style={s.faqToggle}>{openFaq === i ? '−' : '+'}</span>
                </button>
                {openFaq === i && <p style={s.faqA}>{item.a}</p>}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section style={s.ctaSection} ref={ctaRef}>
        <div style={s.ctaInner}>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={ctaInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.65 }}
          >
            <span style={s.label}>Contacto</span>
            <h2 style={s.ctaTitle}>Pronto para antecipar o futuro do seu negócio?</h2>
            <p style={s.ctaBody}>
              Fale com a equipa da 2GF e veja como a Axolote se adapta
              à realidade da sua empresa.
            </p>
            <button style={s.btnPrimary} onClick={goContact}>
              Falar com a equipa
            </button>
          </motion.div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer style={s.footer}>
        <div style={s.footerInner}>
          <span style={s.footerBrand}>
            <span style={{ color: '#0057FF', fontWeight: 900 }}>2GF</span>{' '}
            Innovation Systems
          </span>
          <span style={s.footerCopy}>© {new Date().getFullYear()} Todos os direitos reservados</span>
        </div>
      </footer>

    </div>
  );
};

const s: { [key: string]: React.CSSProperties } = {
  page: { background: '#ffffff', minHeight: '100vh' },

  nav: {
    position: 'sticky',
    top: 0,
    zIndex: 100,
    background: 'rgba(2,15,30,0.97)',
    backdropFilter: 'blur(12px)',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
  },
  navInner: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 2rem',
    height: '85px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  navLogo: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
  },
  navLogoMark: {
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    fontSize: '1.5rem',
    fontWeight: 900,
    color: '#0057FF',
    letterSpacing: '-0.02em',
  },
  navLogoText: {
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    fontSize: '0.875rem',
    fontWeight: 600,
    color: 'rgba(255,255,255,0.85)',
  },
  backBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.4rem',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontFamily: "'IBM Plex Sans', sans-serif",
    fontSize: '0.8rem',
    fontWeight: 500,
    color: 'rgba(255,255,255,0.55)',
  },

  hero: {
    background: '#ffffff',
    padding: '100px 0',
    borderBottom: '1px solid #E8EDF3',
  },
  heroInner: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 2rem',
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '5rem',
    alignItems: 'center',
  },
  heroText: {},
  badge: {
    display: 'inline-block',
    fontFamily: "'IBM Plex Sans', sans-serif",
    fontSize: '0.7rem',
    fontWeight: 600,
    letterSpacing: '0.15em',
    textTransform: 'uppercase',
    color: '#64748B',
    background: '#F1F5F9',
    padding: '0.3rem 0.8rem',
    marginBottom: '1.25rem',
  },
  heroTitle: {
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    fontSize: 'clamp(3rem, 6vw, 5rem)',
    fontWeight: 900,
    color: '#0D1B2A',
    letterSpacing: '-0.04em',
    lineHeight: 1,
    marginBottom: '1rem',
  },
  heroTagline: {
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    fontSize: '1.05rem',
    fontWeight: 600,
    color: '#334155',
    lineHeight: 1.5,
    marginBottom: '1.25rem',
  },
  heroBody: {
    fontSize: '0.95rem',
    color: '#64748B',
    lineHeight: 1.85,
    marginBottom: '1rem',
  },
  heroActions: {
    display: 'flex',
    gap: '1.5rem',
    alignItems: 'center',
    marginTop: '2rem',
    flexWrap: 'wrap',
  },
  heroImgWrap: { overflow: 'hidden', lineHeight: 0 },
  heroImg: {
    width: '100%',
    height: '440px',
    objectFit: 'cover',
    display: 'block',
    filter: 'grayscale(15%)',
  },

  btnPrimary: {
    fontFamily: "'IBM Plex Sans', sans-serif",
    fontSize: '0.8rem',
    fontWeight: 700,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: 'white',
    background: '#7C3AED',
    padding: '0.9rem 2rem',
    border: 'none',
    cursor: 'pointer',
  },
  btnText: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.35rem',
    fontFamily: "'IBM Plex Sans', sans-serif",
    fontSize: '0.8rem',
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: '#7C3AED',
    background: 'none',
    border: 'none',
    borderBottom: '1px solid #7C3AED',
    paddingBottom: '2px',
    cursor: 'pointer',
  },

  statsBar: {
    background: '#ffffff',
    borderBottom: '1px solid #E8EDF3',
  },
  statsInner: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 2rem',
    display: 'flex',
  },
  statItem: {
    padding: '2.5rem 3rem 2.5rem 0',
    marginRight: '3rem',
    borderRight: '1px solid #E8EDF3',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.3rem',
  },
  statVal: {
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    fontSize: '2rem',
    fontWeight: 900,
    color: '#0D1B2A',
    letterSpacing: '-0.03em',
    lineHeight: 1,
  },
  statLabel: {
    fontFamily: "'IBM Plex Sans', sans-serif",
    fontSize: '0.75rem',
    fontWeight: 500,
    color: '#94A3B8',
    letterSpacing: '0.04em',
  },

  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 2rem',
  },
  sectionHeader: { marginBottom: '3.5rem' },
  label: {
    fontFamily: "'IBM Plex Sans', sans-serif",
    fontSize: '0.7rem',
    fontWeight: 600,
    letterSpacing: '0.25em',
    textTransform: 'uppercase',
    color: '#7C3AED',
    display: 'block',
    marginBottom: '1rem',
  },
  sectionTitle: {
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    fontSize: 'clamp(1.6rem, 3vw, 2.25rem)',
    fontWeight: 800,
    color: '#0D1B2A',
    letterSpacing: '-0.02em',
    lineHeight: 1.2,
    maxWidth: '600px',
  },

  featuresSection: {
    background: '#ffffff',
    borderBottom: '1px solid #E8EDF3',
    padding: '100px 0',
  },
  featuresGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    borderTop: '1px solid #E8EDF3',
    borderLeft: '1px solid #E8EDF3',
  },
  featureItem: {
    padding: '2.25rem 2rem',
    borderRight: '1px solid #E8EDF3',
    borderBottom: '1px solid #E8EDF3',
  },
  featureTitle: {
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    fontSize: '0.95rem',
    fontWeight: 700,
    color: '#0D1B2A',
    marginBottom: '0.5rem',
    letterSpacing: '-0.01em',
  },
  featureDesc: {
    fontSize: '0.875rem',
    color: '#64748B',
    lineHeight: 1.75,
    margin: 0,
  },

  whySection: {
    background: '#F7F9FC',
    borderBottom: '1px solid #E8EDF3',
    padding: '100px 0',
  },
  whyGrid: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 2rem',
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '5rem',
    alignItems: 'start',
  },
  whyBody: {
    fontSize: '0.95rem',
    color: '#64748B',
    lineHeight: 1.85,
    marginTop: '1.25rem',
  },
  pillarsList: {
    display: 'flex',
    flexDirection: 'column',
  },
  pillarItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.85rem',
    padding: '1.1rem 0',
    borderBottom: '1px solid #E8EDF3',
  },
  pillarText: {
    fontFamily: "'IBM Plex Sans', sans-serif",
    fontSize: '0.9rem',
    color: '#334155',
  },

  faqSection: {
    background: '#ffffff',
    borderBottom: '1px solid #E8EDF3',
    padding: '100px 0',
  },
  faqContainer: {
    maxWidth: '780px',
    margin: '0 auto',
    padding: '0 2rem',
  },
  faqList: { borderTop: '1px solid #E8EDF3' },
  faqItem: { borderBottom: '1px solid #E8EDF3' },
  faqQ: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1.4rem 0',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    fontSize: '0.95rem',
    fontWeight: 600,
    color: '#0D1B2A',
    textAlign: 'left',
    gap: '1rem',
  },
  faqToggle: {
    fontSize: '1.2rem',
    color: '#94A3B8',
    flexShrink: 0,
    fontWeight: 300,
  },
  faqA: {
    paddingBottom: '1.5rem',
    fontSize: '0.9rem',
    color: '#64748B',
    lineHeight: 1.85,
    margin: 0,
  },

  ctaSection: {
    background: '#F7F9FC',
    borderBottom: '1px solid #E8EDF3',
    padding: '100px 0',
  },
  ctaInner: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 2rem',
  },
  ctaTitle: {
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    fontSize: 'clamp(1.75rem, 3vw, 2.5rem)',
    fontWeight: 800,
    color: '#0D1B2A',
    letterSpacing: '-0.02em',
    lineHeight: 1.2,
    marginTop: '0.75rem',
    marginBottom: '1rem',
    maxWidth: '600px',
  },
  ctaBody: {
    fontSize: '0.95rem',
    color: '#64748B',
    lineHeight: 1.85,
    marginBottom: '2rem',
    maxWidth: '500px',
  },

  footer: {
    background: '#020F1E',
    padding: '2rem 0',
  },
  footerInner: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 2rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  footerBrand: {
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    fontSize: '0.85rem',
    color: 'rgba(255,255,255,0.5)',
  },
  footerCopy: {
    fontFamily: "'IBM Plex Sans', sans-serif",
    fontSize: '0.75rem',
    color: 'rgba(255,255,255,0.3)',
  },
};

export default AxolotePage;
